// Client-side parser for pasted AliExpress-like HTML
// Extracts title, compare-at (original) price, current price, color variants, left gallery images, and video URL (if present)
// Returns: { title, price, priceSource: 'original'|'current'|'none', variants, gallery, video, suggestedFolderName, warnings }

const selectorMap = {
  title: 'h1[data-pl="product-title"]',
  original: '.price-default--original--CWcHOit bdi',
  current: '.price-default--current--F8OlYIo',
  variants: '.sku-item--skus--StEhULs img',
  variantSoldOutClass: 'sku-item--soldOut--YJfuCGq',
};

const leftGallerySelector = '.pdp-info-left img';
const largePreviewClassHint = 'magnifier--image';
const videoIconClassHint = 'slider--videoIcon--WNGL6jY';
// Variant parsing selectors for AliExpress-like DOM
const variantBlockSelector = '.sku-item--property--HuasaIz';
const variantTitleSelector = '.sku-item--title--Z0HLO87';
const imageValueImgSelector = '.sku-item--image--jMUnnGA img';
const textValueSelector = '.sku-item--text--hYfAukP';

const dhgateSelectors = {
  title: '.UiW2OOi h1',
  price: '.ysFrBNT .d7JTIMnD',
  originalPrice: '.ysFrBNT s',
  galleryThumbs: '.Z56lbX2 img[src]',
  galleryMain: '#masterImg[src]',
  galleryVariant: '.Cz5AKaP img[src]',
  galleryDetail: '.GncWtgN img[src]',
  variantRows: '.Crisyr8',
  variantLabel: '.N3B8E2B span',
  variantValues: '.Cz5AKaP[data-attrid][data-attrvalid] img[alt], .Cz5AKaP[data-attrid][data-attrvalid] img[title]',
};

function parseVideo(doc) {
  try {
    // Prefer <video><source src=...></video>
    const sourceEl = doc.querySelector('video source[src]');
    if (sourceEl && sourceEl.getAttribute('src')) {
      return (sourceEl.getAttribute('src') || '').trim();
    }
    // Fallback: <video src=...>
    const videoEl = doc.querySelector('video[src]');
    if (videoEl && videoEl.getAttribute('src')) {
      return (videoEl.getAttribute('src') || '').trim();
    }
    // Fallback: any <source type^="video/"> with src
    const anySource = doc.querySelector('source[type^="video/"][src]');
    if (anySource && anySource.getAttribute('src')) {
      return (anySource.getAttribute('src') || '').trim();
    }
  } catch (_) {}
  return '';
}

function normalizeHttpUrl(url) {
  const raw = (url || '').trim();
  if (!raw) return '';
  if (/^\/\//.test(raw)) return `https:${raw}`;
  return raw;
}

function isLikelyVideoUrl(url) {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return false;
  return /\.(mp4|webm|m3u8)(\?|$)/i.test(url);
}

function parseDhgateVideo(doc) {
  const primary = parseVideo(doc);
  if (primary) return normalizeHttpUrl(primary);

  const attrCandidates = [];
  const attrNodes = Array.from(doc.querySelectorAll('[data-video], [data-video-url], [data-src]'));
  for (const node of attrNodes) {
    const values = [
      node.getAttribute('data-video'),
      node.getAttribute('data-video-url'),
      node.getAttribute('data-src'),
      node.getAttribute('href'),
      node.getAttribute('src'),
    ];
    for (const value of values) {
      const normalized = normalizeHttpUrl(value || '');
      if (isLikelyVideoUrl(normalized)) return normalized;
      if (normalized) attrCandidates.push(normalized);
    }
  }

  const textChunks = [];
  const scriptNodes = Array.from(doc.querySelectorAll('script'));
  for (const script of scriptNodes) {
    const text = script.textContent || '';
    if (text) textChunks.push(text);
  }
  textChunks.push(...attrCandidates);

  const mediaRegex = /https?:\/\/[^"'\s]+?\.(?:mp4|webm|m3u8)(?:\?[^"'\s]*)?/gi;
  for (const chunk of textChunks) {
    const match = chunk.match(mediaRegex);
    if (match && match[0]) {
      return normalizeHttpUrl(match[0]);
    }
  }

  return '';
}

function parseCurrency(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Keep digits, dot, comma, minus; then remove commas and parse float
  const cleaned = trimmed.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  const value = parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

function hasSoldOutAncestor(el, cls) {
  try {
    let n = el;
    for (let i = 0; i < 5 && n; i += 1) {
      if (n.classList && n.classList.contains(cls)) return true;
      n = n.parentElement;
    }
  } catch (_) {}
  return false;
}

function parseVariants(doc) {
  const imgs = Array.from(doc.querySelectorAll(selectorMap.variants));
  const out = [];
  const seen = new Set();
  for (const img of imgs) {
    const name = (img.getAttribute('alt') || '').trim();
    const imageRaw = (img.getAttribute('src') || '').trim();
    if (!name || !imageRaw) continue;
    const disabled = hasSoldOutAncestor(img, selectorMap.variantSoldOutClass);
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const image = normalizeImageUrl(imageRaw);
    out.push({ name, image, disabled });
    if (out.length >= 50) break; // safety cap
  }
  return out;
}

function normalizeImageUrl(u) {
  const s = (u || '').trim();
  if (!s) return s;
  return preferAli960(s);
}

// Upgrade AliExpress CDN thumbnails/previews to 960x960 AVIF consistently
function preferAli960(url) {
  let v = url.trim();

  // Normalize protocol-less URLs
  if (/^\/\//.test(v)) v = 'https:' + v;

  // Replace any _<WxH>q<q>.jpg_.webp or .avif (with optional query) with 960x960 AVIF
  v = v.replace(/_(\d+)x(\d+)q\d+\.jpg_\.(?:webp|avif)(?:\?.*)?$/i, '_960x960q75.jpg_.avif');

  // If captured as plain jpg size suffix, normalize to AVIF variant
  v = v.replace(/_(\d+)x(\d+)q\d+\.jpg(?:\?.*)?$/i, '_960x960q75.jpg_.avif');

  return v;
}

function parseLeftGallery(doc) {
  const nodes = Array.from(doc.querySelectorAll(leftGallerySelector));
  const urls = [];
  const seen = new Set();
  for (const img of nodes) {
    const cls = (img.getAttribute('class') || '').toString();
    if (cls.includes(videoIconClassHint)) continue;

    const src = normalizeImageUrl(img.getAttribute('src'));
    if (!src) continue;
    if (/^data:/i.test(src)) continue; // skip data URIs
    if (seen.has(src)) continue;
    urls.push({ src, el: img });
    seen.add(src);
    if (urls.length >= 30) break; // safety cap
  }
  // Prefer magnifier/large preview image first if present
  const idx = urls.findIndex(x => (x.el?.className || '').toString().includes(largePreviewClassHint));
  if (idx > 0) {
    const [big] = urls.splice(idx, 1);
    urls.unshift(big);
  }
  return urls.map(x => x.src);
}

function safeFolderNameFromTitle(t) {
  const s = (t || '').slice(0, 20).toLowerCase().trim();
  // allow a-z, 0-9, space, dash, underscore; then swap spaces for dash and collapse dashes
  const kept = s.replace(/[^a-z0-9\s-_]/g, '');
  return kept.replace(/\s+/g, '-').replace(/-+/g, '-');
}

function normalizeTitleText(rawTitle) {
  return (rawTitle || '')
    .replace(/Buyers'\s*Picks/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeAliTitle(rawTitle) {
  const normalized = normalizeTitleText(rawTitle);
  if (!normalized) return '';
  return normalized.replace(/\s*-\s*AliExpress(?:\s+\d+)?\s*$/i, '').trim();
}

function parseAliBootstrapTitle(html) {
  const text = String(html || '');
  if (!text) return '';

  const patterns = [
    /"subject"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/i,
    /"productTitle"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/i,
    /"title"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/i,
    /"seoTitle"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/i,
  ];

  for (const pattern of patterns) {
    const match = pattern.exec(text);
    if (!match || !match[1]) continue;
    try {
      const decoded = JSON.parse(`"${match[1]}"`);
      const title = normalizeAliTitle(decoded);
      if (title) return title;
    } catch (_) {
      const title = normalizeAliTitle(match[1]);
      if (title) return title;
    }
  }

  return '';
}

function parseAliTitle(doc, html) {
  const selectorTitle = normalizeAliTitle(doc.querySelector(selectorMap.title)?.textContent || '');
  if (selectorTitle) return { title: selectorTitle, titleSource: 'selector' };

  const ogTitle = normalizeAliTitle(doc.querySelector('meta[property="og:title"]')?.getAttribute('content') || '');
  if (ogTitle) return { title: ogTitle, titleSource: 'og:title' };

  const metaTitle = normalizeAliTitle(doc.querySelector('meta[name="title"]')?.getAttribute('content') || '');
  if (metaTitle) return { title: metaTitle, titleSource: 'meta' };

  const bootstrapTitle = parseAliBootstrapTitle(html);
  if (bootstrapTitle) return { title: bootstrapTitle, titleSource: 'bootstrap' };

  return { title: '', titleSource: 'none' };
}

function isAliChallengePage(doc, html) {
  const text = String(html || '');
  if (/J_MIDDLEWARE_FRAME_WIDGET|_____tmd_____\/punish|captcharecaptcha/i.test(text)) return true;
  if (doc.querySelector('.J_MIDDLEWARE_FRAME_WIDGET')) return true;
  return !!doc.querySelector('.sk1, .sk2, .sk-action-wrap');
}

function parsePriceRange(text) {
  const raw = (text || '').trim();
  if (!raw) return { price: null, maxPrice: null };
  const numeric = raw.match(/\d+(?:\.\d+)?/g) || [];
  if (!numeric.length) return { price: null, maxPrice: null };
  const numbers = numeric.map((n) => Number.parseFloat(n)).filter(Number.isFinite);
  if (!numbers.length) return { price: null, maxPrice: null };
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  return { price: min, maxPrice: max === min ? null : max };
}

function buildDhgateGallery(productDoc, descriptionDoc) {
  const ordered = [];
  const seen = new Set();

  const normalizeDhgateSizedImageUrl = (url) => {
    const normalized = normalizeHttpUrl(url);
    if (!normalized) return '';
    return normalized.replace(/\/(?:100x100|200x200)\//gi, '/0x0/');
  };

  const add = (url) => {
    const normalized = normalizeHttpUrl(url);
    if (!normalized) return;
    if (/^data:/i.test(normalized)) return;
    if (!/^https?:\/\//i.test(normalized)) return;
    if (seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push(normalized);
  };

  add(productDoc.querySelector(dhgateSelectors.galleryMain)?.getAttribute('src') || '');

  Array.from(productDoc.querySelectorAll(dhgateSelectors.galleryThumbs)).forEach((img) => add(normalizeDhgateSizedImageUrl(img.getAttribute('src'))));
  Array.from(productDoc.querySelectorAll(dhgateSelectors.galleryVariant)).forEach((img) => add(normalizeDhgateSizedImageUrl(img.getAttribute('src'))));
  Array.from(descriptionDoc.querySelectorAll(dhgateSelectors.galleryDetail)).forEach((img) => add(img.getAttribute('src')));

  return ordered.slice(0, 30);
}

function parseDhgateVariantProps(doc) {
  const props = [];
  const rows = Array.from(doc.querySelectorAll(dhgateSelectors.variantRows));

  const normalizeDhgateVariantImageUrl = (url) => {
    const normalized = normalizeHttpUrl(url);
    if (!normalized) return '';
    return normalized.replace(/\/(?:100x100|200x200)\//gi, '/0x0/');
  };

  for (const row of rows) {
    const labelEl = row.querySelector(dhgateSelectors.variantLabel);
    const propNameRaw = (labelEl?.textContent || '').replace(':', '').trim();
    if (!propNameRaw) continue;

    const seen = new Set();
    const values = [];
    const items = Array.from(row.querySelectorAll('.Cz5AKaP[data-attrid][data-attrvalid]'));

    for (const item of items) {
      const image = item.querySelector('img');
      if (!image) continue;
      const valueName = (image.getAttribute('alt') || image.getAttribute('title') || '').trim();
      const valueId = (item.getAttribute('data-attrvalid') || slugifyKey(valueName)).trim();
      if (!valueName || !valueId || seen.has(valueId)) continue;
      seen.add(valueId);
      values.push({
        id: slugifyKey(valueId),
        name: valueName,
        image: normalizeDhgateVariantImageUrl(image.getAttribute('src') || ''),
        disabled: false,
      });
      if (values.length >= 200) break;
    }

    if (!values.length) continue;
    props.push({
      id: slugifyKey(propNameRaw),
      name: propNameRaw,
      type: 'image',
      displayOrder: props.length,
      values,
    });
  }

  return props;
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseDhgateDescription(doc) {
  const root = doc.querySelector('.GncWtgN') || doc.body;
  if (!root) return '';

  Array.from(root.querySelectorAll('script, style, noscript, iframe, object, embed, link')).forEach((node) => {
    node.remove();
  });

  const paragraphNodes = Array.from(root.querySelectorAll('.detailmodule_text p, p'));
  const tableNodes = Array.from(root.querySelectorAll('table, dl'));
  const segments = [];

  paragraphNodes.forEach((node) => {
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) segments.push(text);
  });

  tableNodes.forEach((node) => {
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) segments.push(text);
  });

  if (!segments.length) {
    const fallbackText = (root.textContent || '').replace(/\s+/g, ' ').trim();
    if (fallbackText) segments.push(fallbackText);
  }

  return segments.slice(0, 20).map((line) => `<p>${escapeHtml(line)}</p>`).join('');
}

// Slugify IDs for properties and values (kebab-case, ASCII, max length)
function slugifyKey(str = '', max = 32) {
  return String(str)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, max || 32);
}

function parseVariantProps(doc) {
  const blocks = Array.from(doc.querySelectorAll(variantBlockSelector));
  const props = [];
  for (const block of blocks) {
    const titleEl = block.querySelector(variantTitleSelector);
    if (!titleEl) continue;
    let rawTitle = (titleEl.textContent || '').trim();
    if (!rawTitle) continue;
    // Normalize title: strip trailing ": <current>" and normalize spaces around parentheses
    let name = rawTitle.replace(/:\s*.*$/, '').replace(/\s*\(([^)]*)\)\s*/g, ' ($1)').trim();
    if (!name) continue;

    const images = Array.from(block.querySelectorAll(imageValueImgSelector));
    const texts = Array.from(block.querySelectorAll(textValueSelector));
    const type = images.length > 0 ? 'image' : 'text';

    const values = [];
    const seen = new Set();
    if (type === 'image') {
      for (const img of images) {
        const vname = (img.getAttribute('alt') || '').trim();
        const raw = (img.getAttribute('src') || '').trim();
        if (!vname || !raw) continue;
        const id = slugifyKey(vname) || slugifyKey(raw);
        if (seen.has(id)) continue;
        seen.add(id);
        const image = normalizeImageUrl(raw);
        const disabled = hasSoldOutAncestor(img, selectorMap.variantSoldOutClass);
        const hot = !!img.closest('[class*="showHot"], [class*="active"], [aria-pressed="true"]');
        values.push({ id, name: vname, image, hot, disabled });
        if (values.length >= 200) break;
      }
    } else {
      for (const node of texts) {
        const vname = (node.textContent || '').trim();
        if (!vname) continue;
        const id = slugifyKey(vname);
        if (seen.has(id)) continue;
        seen.add(id);
        const disabled = hasSoldOutAncestor(node, selectorMap.variantSoldOutClass);
        const hot = (node.className || '').includes('showHot') || (node.getAttribute('aria-pressed') === 'true');
        values.push({ id, name: vname, hot, disabled });
        if (values.length >= 200) break;
      }
    }
    if (!values.length) continue;
    props.push({
      id: slugifyKey(name.replace(/\s+/g, ' ')),
      name,
      type,
      displayOrder: props.length,
      values,
    });
    if (props.length >= 5) break; // safety cap for number of properties
  }
  return props;
}

function cartesian(arrays) {
  return arrays.reduce((acc, curr) => {
    if (!curr.length) return acc;
    const next = [];
    for (const a of acc) for (const b of curr) next.push([...a, b]);
    return next;
  }, [[]]);
}

function generateSkus(variantProps) {
  if (!Array.isArray(variantProps) || !variantProps.length) return [];
  const lists = variantProps.map((p) => (p.values || []).map((v) => ({ propId: p.id, value: v })));
  if (lists.some((l) => l.length === 0)) return [];
  const combos = cartesian(lists);

  // Prefer first image-type prop (e.g., Color) to supply SKU image
  const imageProp = variantProps.find((p) => p.type === 'image');

  return combos.map((combo) => {
    const key = {};
    let image;
    let unavailable = false;
    for (const { propId, value } of combo) {
      key[propId] = value.id;
      if (!image && imageProp && propId === imageProp.id && value.image) image = value.image;
      if (value.disabled) unavailable = true;
    }
    return {
      key,
      price: undefined, // fallback to base price at read-time
      countInStock: 10,
      image,
      unavailable,
    };
  });
}

export function parseAliHtml(html) {
  const warnings = [];
  try {
    if (!html || typeof html !== 'string') {
      return { title: '', titleSource: 'none', price: null, priceSource: 'none', variants: [], gallery: [], suggestedFolderName: '', warnings: ['Empty HTML'] };
    }
    if (html.length > 2_000_000) {
      warnings.push('HTML too large; parsing may be incomplete');
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const { title, titleSource } = parseAliTitle(doc, html);
    const suggestedFolderName = title ? safeFolderNameFromTitle(title) : '';

    const originalText = doc.querySelector(selectorMap.original)?.textContent || '';
    const currentText = doc.querySelector(selectorMap.current)?.textContent || '';

    const compareAtPrice = parseCurrency(originalText);
    const currentPrice = parseCurrency(currentText);

    const price = (compareAtPrice ?? currentPrice) ?? null;
    const priceSource = compareAtPrice != null ? 'original' : (currentPrice != null ? 'current' : 'none');

    const variants = parseVariants(doc); // legacy color images (back-compat)
    const gallery = parseLeftGallery(doc);
    const video = parseVideo(doc);
    const variantProps = parseVariantProps(doc);
    const skus = generateSkus(variantProps);
    const challengePageDetected = isAliChallengePage(doc, html);

    if (!title) warnings.push('Title not found');
    if (titleSource !== 'none' && titleSource !== 'selector') warnings.push(`Title recovered from ${titleSource}`);
    if (price == null) warnings.push('Price not found');
    if (!gallery.length) warnings.push('Gallery not found');
    if (challengePageDetected) warnings.push('Challenge page detected; parser output may be incomplete');

    return { title, titleSource, price, priceSource, variants, variantProps, skus, gallery, video, suggestedFolderName, warnings };
  } catch (err) {
    return { title: '', titleSource: 'none', price: null, priceSource: 'none', variants: [], variantProps: [], skus: [], gallery: [], video: '', suggestedFolderName: '', warnings: ['Parse error'] };
  }
}

export function parseDhgateHtml(productInfoHtml, productDescriptionHtml = '') {
  const warnings = [];
  try {
    const productHtml = typeof productInfoHtml === 'string' ? productInfoHtml : '';
    const detailHtml = typeof productDescriptionHtml === 'string' ? productDescriptionHtml : '';

    if (!productHtml.trim()) {
      return {
        title: '',
        price: null,
        originalPrice: null,
        priceSource: 'none',
        variants: [],
        variantProps: [],
        skus: [],
        gallery: [],
        video: '',
        suggestedFolderName: '',
        description: '',
        warnings: ['Empty DHgate product info HTML'],
        meta: { source: 'dhgate', parsedAt: new Date().toISOString() },
      };
    }

    const parser = new DOMParser();
    const productDoc = parser.parseFromString(productHtml, 'text/html');
    const descriptionDoc = parser.parseFromString(detailHtml || '<div></div>', 'text/html');

    const title = normalizeTitleText(productDoc.querySelector(dhgateSelectors.title)?.textContent || '');
    const suggestedFolderName = title ? safeFolderNameFromTitle(title) : '';

    const parsedPriceRange = parsePriceRange(productDoc.querySelector(dhgateSelectors.price)?.textContent || '');
    const originalPriceRange = parsePriceRange(productDoc.querySelector(dhgateSelectors.originalPrice)?.textContent || '');

    const price = parsedPriceRange.price;
    const originalPrice = originalPriceRange.price;
    const priceSource = price != null ? 'current' : 'none';

    const gallery = buildDhgateGallery(productDoc, descriptionDoc);
    const variantProps = parseDhgateVariantProps(productDoc);
    const skus = generateSkus(variantProps);
    const video = parseDhgateVideo(productDoc) || parseDhgateVideo(descriptionDoc);
    const description = parseDhgateDescription(descriptionDoc);

    if (!title) warnings.push('Title not found');
    if (price == null) warnings.push('Price not found');
    if (!gallery.length) warnings.push('Gallery not found');
    if (!variantProps.length) warnings.push('No variants detected');
    if (!video) warnings.push('Video not detected');

    return {
      title,
      price,
      originalPrice,
      priceSource,
      variants: [],
      variantProps,
      skus,
      gallery,
      video,
      suggestedFolderName,
      description,
      warnings,
      meta: {
        source: 'dhgate',
        parsedAt: new Date().toISOString(),
        counts: {
          gallery: gallery.length,
          variantProps: variantProps.length,
          skus: skus.length,
        },
      },
    };
  } catch (err) {
    return {
      title: '',
      price: null,
      originalPrice: null,
      priceSource: 'none',
      variants: [],
      variantProps: [],
      skus: [],
      gallery: [],
      video: '',
      suggestedFolderName: '',
      description: '',
      warnings: ['Parse error'],
      meta: { source: 'dhgate', parsedAt: new Date().toISOString() },
    };
  }
}

export const aliSelectorMap = selectorMap;
